import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  PLATFORM_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
  REQUEST
} from '@angular/core';
import {provideRouter, withInMemoryScrolling} from '@angular/router';

import {routes} from './app.routes';
import {provideClientHydration, withIncrementalHydration} from '@angular/platform-browser';
import {HttpContextToken, provideHttpClient, withFetch, withInterceptors} from '@angular/common/http';
import {PUBLIC, TranslocoHttpLoader} from './transloco-loader';
import {provideTransloco, TranslocoService} from '@jsverse/transloco';
import {ApiModule, Configuration} from "../../api/lib";
import {provideAnimationsAsync} from "@angular/platform-browser/animations/async";
import {environment} from "../environments/environment";
import {AuthenticationService} from "@services";
import {routes as BackofficeRoutes} from './pages/backoffice/routes'
import {LayoutService} from "@services";
import {finalize, throwError} from "rxjs";
import {catchError} from "rxjs/operators";
import { EditorModule } from '@tinymce/tinymce-angular';
import { isPlatformBrowser } from '@angular/common';

export const SHOW_SPINNER = new HttpContextToken<boolean>(() => false);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withInMemoryScrolling({
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled'
    })),
    provideRouter(BackofficeRoutes),
    provideClientHydration(withIncrementalHydration()),
    provideHttpClient(withFetch(), withInterceptors(
      [
        (req, next) => {
          const layoutService = inject(LayoutService);
          const authenticationService = inject(AuthenticationService)
          const request = inject(REQUEST);
          const showSpinner = req.context.get(SHOW_SPINNER);

          if (req.context.get(PUBLIC)) {
            return next(req);
          }
          if (showSpinner)
            layoutService.increment()

          let headers: Record<string, any> = {}
          if (authenticationService.token()) {
            headers['Authorization'] = `Bearer ${authenticationService.token()}`;
          }

          if (request && (request?.headers as any)?.['cookie'])
            headers['cookie'] = (request?.headers as any)?.['cookie'];

          return next(req.clone(
            {
              withCredentials: true,
              ...(Object.keys(headers).length > 0 ? {setHeaders: headers} : {})
            }
          )).pipe(
            catchError((error) => {
              return throwError(() => error)
            }),
            finalize(() => {
              if (showSpinner)
                layoutService.decrement()
            })
          )
        }
      ]
    )),
    provideAnimationsAsync(),
    importProvidersFrom(
      ApiModule.forRoot(() => {
        return new Configuration({
          basePath: environment.baseUrl,
        })
      })
    ),
    provideAppInitializer(() => {
      const authService = inject(AuthenticationService)
      authService.checkToken();
    }),
    provideAppInitializer(() => {
      const translocoService = inject(TranslocoService);
      const platform = inject(PLATFORM_ID);
      if (isPlatformBrowser(platform)) {
        const savedLang = localStorage.getItem('__lang__');
        if (savedLang) {
          translocoService.setActiveLang(savedLang);
        }
      }
    }),
    importProvidersFrom(EditorModule),
    provideTransloco({
      config: {
        availableLangs: ['it', 'en'],
        defaultLang: 'it',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    })],
};
